import { useCallback, useEffect, useRef, useState } from 'react';

import type { NdjsonBlock } from '../lib/ndjsonBlockTypes';
import { sessionsService } from '../services/sessionsService';

const SOFT_TIMEOUT_MS = 60_000;

export interface StreamError {
  source: 'note' | 'report';
  message: string;
}

export interface UseSessionSummaryStreamResult {
  noteBlocks: NdjsonBlock[];
  reportBlocks: NdjsonBlock[];
  done: boolean;
  timedOut: boolean;
  errors: StreamError[];
}

/**
 * Manage the SSE lifecycle for the post-session AI summary stream.
 *
 * - Opens the stream when `enabled` is true and `streamUrl` is non-null.
 * - Accumulates note + case-report blocks as they arrive.
 * - `done` flips true on the server's terminal {kind:'done'} event.
 * - `timedOut` flips true after 60s of silence (no events); resets on
 *   the next event. The parent decides what to do on timeout (refetch
 *   bundle, show fallback CTA, etc.).
 * - `errors` accumulates server-emitted `{kind:'error'}` events without
 *   tearing the stream down — the other side may still complete.
 */
export function useSessionSummaryStream(input: {
  streamUrl: string | null;
  enabled: boolean;
}): UseSessionSummaryStreamResult {
  const [noteBlocks, setNoteBlocks] = useState<NdjsonBlock[]>([]);
  const [reportBlocks, setReportBlocks] = useState<NdjsonBlock[]>([]);
  const [done, setDone] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [errors, setErrors] = useState<StreamError[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const armTimeout = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setTimedOut(true);
    }, SOFT_TIMEOUT_MS);
  }, []);

  useEffect(() => {
    if (!input.enabled || input.streamUrl === null) return;

    const controller = new AbortController();
    armTimeout();

    void sessionsService
      .streamSessionSummary({
        streamUrl: input.streamUrl,
        signal: controller.signal,
        handlers: {
          onNoteBlock: (block) => {
            setNoteBlocks((prev) => [...prev, block]);
            setTimedOut(false);
            armTimeout();
          },
          onReportBlock: (block) => {
            setReportBlocks((prev) => [...prev, block]);
            setTimedOut(false);
            armTimeout();
          },
          onDone: () => {
            setDone(true);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
          },
          onStreamError: (source, message) => {
            setErrors((prev) => [...prev, { source, message }]);
          },
        },
      })
      .catch((err: unknown) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setErrors((prev) => [
          ...prev,
          { source: 'note', message: err instanceof Error ? err.message : 'Stream failed' },
        ]);
      });

    return () => {
      controller.abort();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [input.streamUrl, input.enabled, armTimeout]);

  return { noteBlocks, reportBlocks, done, timedOut, errors };
}
